import { useMemo, useState } from "react"
import { ChatThread } from "@/schema"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { format, isThisYear, isToday } from "date-fns"
import { History } from "lucide-react"

interface ChatThreadHistoryProps {
  threads?: ChatThread[]
  onThreadSelect: (threadId: string) => void
  currentThreadId: string
  disabled?: boolean
}

export default function ChatThreadHistory({
  threads,
  onThreadSelect,
  currentThreadId,
  disabled,
}: ChatThreadHistoryProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const threadPreviews = useMemo(() => {
    const threadsWithMessages = threads?.filter((thread) => {
      const hasMessages = (thread.message_count ?? thread.chat_messages?.length ?? 0) > 0
      const isCurrentThread = currentThreadId === "recent" ? false : thread.id === currentThreadId
      return hasMessages && !isCurrentThread
    })

    return threadsWithMessages
      ?.map((thread) => {
        const firstMessage = thread.first_message_content ?? thread.chat_messages?.[0]?.content ?? ""
        return {
          id: thread.id,
          date: thread.first_message_date ?? thread.chat_messages?.[0]?.created_at,
          firstMessage,
          searchText: firstMessage.toLowerCase(),
        }
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [threads, currentThreadId])

  const filteredThreads = useMemo(() => {
    const query = search.toLowerCase()
    return threadPreviews?.filter((thread) => thread.searchText.includes(query)) ?? []
  }, [threadPreviews, search])

  const hasThreads = threadPreviews?.length && threadPreviews.length > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled || !hasThreads}>
        <Button variant="ghost" size="iconXs" disabled={disabled || !hasThreads} aria-label="Chat history">
          <History className="text-icon" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="mt-1 w-100 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search chat history..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-60">
            <CommandEmpty className="text-muted-foreground p-2 text-center text-xs">
              No chat threads found.
            </CommandEmpty>
            <CommandGroup>
              {filteredThreads.map((thread) => {
                const isTodayDate = isToday(thread.date)
                const isCurrentYear = isThisYear(thread.date)
                const date = isTodayDate
                  ? format(thread.date, "h:mm a")
                  : isCurrentYear
                    ? format(thread.date, "MMM do")
                    : format(thread.date, "MMM d, yyyy")
                return (
                  <CommandItem
                    key={thread.id}
                    value={thread.id}
                    onSelect={() => {
                      onThreadSelect(thread.id)
                      setOpen(false)
                    }}
                    className="hover:bg-muted cursor-pointer truncate bg-transparent"
                    disableHighlighting
                  >
                    <div className="grid w-full grid-cols-[1fr_80px] items-center justify-between gap-2">
                      <span className="truncate text-sm">{thread.firstMessage}</span>
                      <span className="text-muted-foreground flex-shrink-0 text-right text-xs">{date}</span>
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
